/**
 * Drives a realtime game's continuous local simulation for a networked
 * (multi-client) room. `tick` can never be a relayed room move in this mode
 * — every client would log its own tick into the shared action log, running
 * the simulation at N-times speed with non-deterministic interleaving, and
 * every client replays the full action log on every broadcast (see
 * roomConnection.ts's reconstruct()), so logging thousands of ticks would be
 * disastrous. Instead each client runs its own local copy of the game's own
 * `advanceOneTick`, and reconciles it against the room's replayed G (which
 * only reflects logged, discrete moves, never `tick`) via
 * `applyIncomingState`.
 *
 * This module owns 100% of the scheduling: the fixed-timestep tick loop,
 * the periodic checkpoint timer, and the "only treat a checkpoint as
 * confirmed once it round-trips back through applyIncomingState — never
 * bump anything on submission itself" invariant (see submitCheckpoint's
 * comment; getting this wrong was a real, hard-to-spot bug during Spacewar!'s
 * development — both clients' periodic timers fire on the same cadence
 * regardless of which submission the room actually accepts, so optimistic
 * local bookkeeping on submission desyncs from what actually got confirmed).
 * A game's `RealtimePlugin` (see @merels/sdk's gameLogic.ts) owns
 * only what's genuinely game-specific: advancing one tick, and reconciling
 * an incoming checkpoint/replay against local state — this module treats
 * both as black boxes and never inspects `G`'s shape itself.
 */
import type { RealtimePlugin } from '@merels/sdk'
import type { RealtimeNetworkedSim } from './realtimeNetworkedSim.types'

// Short enough that drift between corrections stays small even under fast
// continuous motion, long enough not to flood the room with checkpoints.
const CHECKPOINT_INTERVAL_MS = 2000

// How often the tick loop polls elapsed time — deliberately decoupled from
// tickRateHz (mirrors ~60fps, the rate the old requestAnimationFrame-driven
// version polled at regardless of tickRateHz). The fixed-timestep
// accumulator below is what actually paces ticks to tickRateHz; polling
// faster than that just keeps its measurement of elapsed real time fine-
// grained, since setInterval's delay is floored to an integer ms and a
// coarser poll interval (e.g. matching tickRateHz's own, often-fractional
// period) would otherwise accumulate a small systematic rounding deficit.
const TICK_POLL_INTERVAL_MS = 1000 / 60

export function makeRealtimeNetworkedSim<G>(
  initialG: G,
  tickRateHz: number,
  checkpointState: (G: G) => void,
  mySeat: string | undefined,
  plugin: RealtimePlugin<G>,
): RealtimeNetworkedSim<G> {
  // Cloned once, up front: a plugin's advanceOneTick may mutate G in place
  // (see RealtimePlugin's contract), and `initialG` is very likely
  // the same object reference the room/caller holds onto elsewhere (e.g.
  // GameContainer's own `gameState`) — mutating it directly would corrupt
  // whatever else reads that reference. Every G after this one is the
  // sim's own private copy, produced by the plugin's own functions, so no
  // further defensive cloning is needed here.
  let localG = structuredClone(initialG)

  let tickIntervalHandle: ReturnType<typeof setInterval> | null = null
  let lastFrameTime: number | null = null
  let accumulatorMs = 0
  let checkpointIntervalHandle: ReturnType<typeof setInterval> | null = null

  // Fire-and-forget: does NOT mutate `localG` itself, since checkpointState
  // is fire-and-forget and this client can't yet tell whether the room's
  // basedOnSeqIndex guard will actually accept it. See this module's header
  // for why optimistic local bookkeeping here would be a real bug — only a
  // confirmed round trip (this submission's own broadcast coming back
  // through applyIncomingState) may update `localG`.
  function submitCheckpoint(): void {
    checkpointState(plugin.prepareOutgoingCheckpoint(localG, mySeat))
  }

  // setInterval, not requestAnimationFrame: this loop only steps physics, it
  // never paints anything (Game.tsx runs its own independent rAF loop for
  // that), and browsers fully suspend rAF callbacks for a hidden/backgrounded
  // tab — a real bug found in testing, since a tab in the background would
  // stop advancing its own ship entirely while its periodic checkpoint timer
  // (a setInterval, only throttled in the background, never suspended) kept
  // re-submitting the same frozen snapshot every cycle. Every other client
  // then correctly (by design) adopts that submitter's own snapshot for its
  // ship, reading as that ship being permanently stuck replaying the same
  // position. setInterval is only throttled (clamped to a minimum firing
  // rate) in a background tab, never fully suspended, and the fixed-timestep
  // accumulator below catches up on however much real time actually elapsed.
  function tickLoop(): void {
    const now = Date.now()
    if (lastFrameTime !== null) {
      const stepMs = 1000 / tickRateHz
      accumulatorMs += now - lastFrameTime
      while (accumulatorMs >= stepMs) {
        const { G, winnerJustDecided } = plugin.advanceOneTick(localG)
        localG = G
        accumulatorMs -= stepMs
        // Only the assigned reporter for this outcome submits the
        // immediate checkpoint — see RealtimePlugin.shouldReportImmediately.
        if (winnerJustDecided && plugin.shouldReportImmediately?.(localG, mySeat)) submitCheckpoint()
      }
    }
    lastFrameTime = now
  }

  function start(): void {
    stop()
    lastFrameTime = Date.now()
    tickIntervalHandle = setInterval(tickLoop, TICK_POLL_INTERVAL_MS)
    checkpointIntervalHandle = setInterval(submitCheckpoint, CHECKPOINT_INTERVAL_MS)
  }

  function stop(): void {
    if (tickIntervalHandle !== null) {
      clearInterval(tickIntervalHandle)
      tickIntervalHandle = null
    }
    lastFrameTime = null
    accumulatorMs = 0
    if (checkpointIntervalHandle !== null) {
      clearInterval(checkpointIntervalHandle)
      checkpointIntervalHandle = null
    }
  }

  function getG(): G {
    return localG
  }

  function applyIncomingState(incoming: G): void {
    localG = plugin.mergeIncomingState(localG, incoming, mySeat)
  }

  return { start, stop, getG, applyIncomingState }
}
